import { escapeHtml } from '../helpers.js';

/**
 * Extend the MapApp prototype with search UI helpers.
 * @param {typeof import('../app.js').default} App
 */
export function applySearchMixins(App) {
	/**
	 * React to text changes in the search input and re-render the list.
	 */
	App.prototype.onSearchInputChange = function onSearchInputChange() {
		if (!this.searchInputEl) {
			return;
		}
		this.renderSearchResults(this.searchInputEl.value);
	};

	/**
	 * Keyboard handling for the search input (escape clears, enter selects).
	 * @param {KeyboardEvent} event
	 */
	App.prototype.onSearchInputKeydown = function onSearchInputKeydown(event) {
		if (!this.searchResultsEl) {
			return;
		}
		if (event.key === 'Escape') {
			if (this.searchInputEl && this.searchInputEl.value) {
				this.searchInputEl.value = '';
				this.renderSearchResults('');
			}
			event.preventDefault();
		} else if (event.key === 'Enter') {
			const firstResult = this.searchResultsEl.querySelector('.search-result');
			if (firstResult) {
				firstResult.click();
				event.preventDefault();
			}
		}
	};

	/**
	 * Handle click selection of a search result button.
	 * @param {MouseEvent} event
	 */
	App.prototype.onSearchResultsClick = function onSearchResultsClick(event) {
		const button = event.target.closest('.search-result');
		if (!button) {
			return;
		}
		const kind = button.getAttribute('data-kind');
		const slug = button.getAttribute('data-slug');
		if (!kind || !slug) {
			return;
		}
		const entity = this.findEntityBySlug(kind, slug);
		if (!entity) {
			return;
		}
		if (this.searchInputEl) {
			this.searchInputEl.blur();
		}
		this.panMapToEntity(kind, entity);
		this.handleEntitySelection(kind, entity);
	};

	/**
	 * Build a combined list of nations and cities for fuzzy search.
	 */
	App.prototype.buildSearchIndex = function buildSearchIndex() {
		this.searchEntries = [];
		if (Array.isArray(globalThis.countries)) {
			for (let i = 0; i < globalThis.countries.length; i += 1) {
				const country = globalThis.countries[i];
				const entry = this.countryEntryMap ? this.countryEntryMap.get(country) : null;
				const slug = entry && entry.slug ? entry.slug : this.getInfoId(country);
				this.searchEntries.push({
					name: country.name,
					kind: 'nation',
					slug,
					entity: country,
					normalized: this.normalizeSearchText(country.name),
					meta: 'Nation'
				});
			}
		}
		if (Array.isArray(globalThis.cities)) {
			for (let j = 0; j < globalThis.cities.length; j += 1) {
				const city = globalThis.cities[j];
				const cityEntry = this.cityEntryMap ? this.cityEntryMap.get(city) : null;
				const citySlug = cityEntry && cityEntry.slug ? cityEntry.slug : this.getInfoId(city);
				this.searchEntries.push({
					name: city.name,
					kind: 'city',
					slug: citySlug,
					entity: city,
					normalized: this.normalizeSearchText(city.name),
					meta: 'City'
				});
			}
		}
		this.searchEntries.sort((a, b) => a.name.localeCompare(b.name));
	};

	/**
	 * Render visible search results for the provided query text.
	 * @param {string} query
	 */
	App.prototype.renderSearchResults = function renderSearchResults(query) {
		if (!this.searchResultsEl) {
			return;
		}
		const normalized = this.normalizeSearchText(query);
		const matches = [];
		for (let i = 0; i < this.searchEntries.length; i += 1) {
			const entry = this.searchEntries[i];
			if (!normalized || entry.normalized.indexOf(normalized) !== -1) {
				matches.push(entry);
			}
			if (matches.length >= 50) {
				break;
			}
		}
		if (!matches.length) {
			this.searchResultsEl.innerHTML =
				'<li class="search-result search-result--empty">No matches found.</li>';
			return;
		}
		this.searchResultsEl.innerHTML = matches
			.map((entry) => {
				return (
					'<li>' +
					'<button class="search-result" data-kind="' +
					escapeHtml(entry.kind) +
					'" data-slug="' +
					escapeHtml(entry.slug) +
					'">' +
					'<span class="search-result__name">' +
					escapeHtml(entry.name) +
					'</span>' +
					'<span class="search-result__meta">' +
					escapeHtml(entry.meta) +
					'</span>' +
					'</button>' +
					'</li>'
				);
			})
			.join('');
	};

	/**
	 * Highlight the active search result to match the map selection.
	 */
	App.prototype.updateSearchSelection = function updateSearchSelection() {
		if (!this.searchResultsEl) {
			return;
		}
		const activeSlug = this.highlightedState
			? this.highlightedState.entry && this.highlightedState.entry.slug
			: null;
		const buttons = this.searchResultsEl.querySelectorAll('.search-result');
		for (let i = 0; i < buttons.length; i += 1) {
			const button = buttons[i];
			const slug = button.getAttribute('data-slug');
			button.classList.toggle('is-active', slug === activeSlug);
		}
	};

	/**
	 * Normalize a string for case/diacritic-insensitive searching.
	 * @param {string} value
	 * @returns {string}
	 */
	App.prototype.normalizeSearchText = function normalizeSearchText(value) {
		return String(value || '')
			.normalize('NFKD')
			.replace(/[\u0300-\u036f]/g, '')
			.toLowerCase()
			.replace(/[^a-z0-9]+/g, ' ')
			.replace(/\s+/g, ' ')
			.trim();
	};
}
